/**
 * Minimal column definition fields needed to enforce pinning constraints and
 * determine canonical column order. Intentionally decoupled from AG Grid's
 * versioned `ColDef` type so this utility is stable across upgrades.
 */
export type ColDefConstraint = {
  /** Preferred over `field` when both are present. */
  colId?: string;
  /** Fallback identifier when `colId` is absent. */
  field?: string;
  /**
   * Desired pin side. Matches AG Grid's ColDef:
   * - `'left'` | `'right'` — pin to that side
   * - `true` — normalised to `'left'`
   * - `false` | `null` | `undefined` — not pinned
   */
  pinned?: 'left' | 'right' | boolean | null;
  /**
   * When `true`, the pinned state from `pinned` is always enforced even if
   * the user has no explicit `pinned` value set on the colDef.
   */
  lockPinned?: boolean;
};

type ColumnStateLike = Record<string, unknown>;

type NormalisedPin = 'left' | 'right' | null;

function getColId(col: ColDefConstraint): string | undefined {
  return col.colId ?? col.field;
}

function normalisePin(pinned: ColDefConstraint['pinned']): NormalisedPin {
  if (pinned === 'left' || pinned === true) {
    return 'left';
  }
  if (pinned === 'right') {
    return 'right';
  }
  return null;
}

function zonePriority(pinned: unknown): number {
  if (pinned === 'left') {
    return 0;
  }
  if (pinned === 'right') {
    return 2;
  }
  return 1;
}

/**
 * Reconciles a stale localStorage column state with the current column
 * definition list, applying three transformations in order:
 *
 * 1. **Drop removed columns** — any saved entry whose `colId` no longer
 *    appears in `colDefs` is discarded.
 *
 * 2. **Override pins** — for every remaining saved entry whose colDef has
 *    an explicit `pinned` value or `lockPinned: true`, the saved `pinned`
 *    value is replaced with the colDef value. This ensures pinned/locked
 *    columns are always in the correct zone regardless of what the user's
 *    localStorage snapshot says.
 *
 * 3. **Insert new columns** — columns present in `colDefs` but absent from
 *    `savedState` (e.g. a newly added column) are inserted at the position
 *    immediately after the nearest preceding sibling that already exists in
 *    the merged array. When no preceding sibling exists the column is
 *    prepended.
 *
 * 4. **Stable zone sort** — the result is sorted so `pinned: 'left'` entries
 *    come first, unpinned entries in the middle, and `pinned: 'right'` entries
 *    last. The sort is stable, preserving relative user-defined order within
 *    each zone.
 *
 * The return value is suitable for passing directly to AG Grid's
 * `applyColumnState({ state, applyOrder: true })`.
 */

export function mergeColumnState(
  savedState: ColumnStateLike[],
  colDefs: ColDefConstraint[],
): ColumnStateLike[] {
  if (colDefs.length === 0) {
    return [];
  }

  const colDefIdSet = new Set(colDefs.map(getColId).filter(Boolean) as string[]);
  const savedIdSet = new Set(savedState.map((s) => s.colId as string).filter(Boolean));

  // Step 1 + 2: filter removed columns and override pins
  const result: ColumnStateLike[] = savedState
    .filter((s) => colDefIdSet.has(s.colId as string))
    .map((s) => {
      const colId = s.colId as string;
      const colDef = colDefs.find((d) => getColId(d) === colId);
      if (colDef && (colDef.pinned !== undefined || colDef.lockPinned)) {
        return { ...s, pinned: normalisePin(colDef.pinned) };
      }
      return s;
    });

  // Track which colIds are currently in the result for O(1) lookup
  const resultIdSet = new Set(result.map((s) => s.colId as string));

  // Step 3: insert new columns at best-effort position
  for (let defIdx = 0; defIdx < colDefs.length; defIdx++) {
    const colDef = colDefs[defIdx];
    const colId = getColId(colDef);
    if (!colId || savedIdSet.has(colId)) {
      continue; // already in saved state or unnamed
    }

    const newEntry: ColumnStateLike = {
      colId,
      pinned: normalisePin(colDef.pinned),
    };
    // Find the nearest preceding colDef sibling present in the result
    let insertAfterIndex = -1;
    for (let i = defIdx - 1; i >= 0; i--) {
      const prevColId = getColId(colDefs[i]);
      if (!prevColId) {
        continue;
      }
      const idx = result.findIndex((s) => s.colId === prevColId);
      if (idx !== -1) {
        insertAfterIndex = idx;
        break;
      }
    }
    result.splice(insertAfterIndex + 1, 0, newEntry);
    resultIdSet.add(colId);
  }

  // Step 4: stable zone sort (left → center → right)
  result.sort((a, b) => zonePriority(a.pinned) - zonePriority(b.pinned));

  return result;
}